import { authConfig as IDENTITY_CONFIG } from "./authConfig";
import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import { isNotNil } from "./utils";
import jwtDecode from "jwt-decode";
import { set } from "../common/utils";

class AuthServices {
  UserManager: UserManager;
  private _lockSignInSilentMethod: boolean;

  private STORAGE_REDIRECT_URI_KEY = "redirectUri";

  constructor() {
    this._lockSignInSilentMethod = false;
    //@ts-ignore
    this.UserManager = new UserManager({
      ...IDENTITY_CONFIG,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    });

    this.UserManager.events.addUserLoaded(() => {
      if (window.location.href.indexOf("callback") !== -1) {
        this.navigateToScreen();
      }
    });
    this.UserManager.events.addSilentRenewError((e) => {
      // eslint-disable-next-line no-console
      console.log("Silent renew error", e.message);
    });

    this.UserManager.events.addAccessTokenExpired(() => {
      // eslint-disable-next-line no-console
      console.log("Token expired");
      this.signinSilent();
    });
  }

  signinRedirectCallback = () => {
    this.UserManager.signinRedirectCallback().then(() => {
      // ('');
    });
  };

  getUser = async () => {
    const user = await this.UserManager.getUser();
    if (!user) {
      return await this.UserManager.signinRedirectCallback();
    }
    return user;
  };

  parseJwt = (token: string) => {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace("-", "+").replace("_", "/");
    return JSON.parse(window.atob(base64));
  };

  signinRedirect = () => {
    let pathname = window.location.pathname;
    if (window.location.search) {
      pathname = pathname + window.location.search;
    }
    localStorage.setItem(this.STORAGE_REDIRECT_URI_KEY, pathname);
    this.UserManager.signinRedirect();
  };

  navigateToScreen = () => {
    const redirectUri =
      window.localStorage.getItem(this.STORAGE_REDIRECT_URI_KEY) || "/";
    window.location.replace(redirectUri);
  };

  isAuthenticated = () => {
    const oidcStorage = sessionStorage.getItem(
      `oidc.user:${IDENTITY_CONFIG.authority}:${IDENTITY_CONFIG.client_id}`
    );

    const hasToken =
      isNotNil(oidcStorage) && isNotNil(JSON.parse(oidcStorage).access_token);

    if (!hasToken) {
      return false;
    }
    try {
      const decodedToken = jwtDecode(JSON.parse(oidcStorage).id_token);
      const currentTime = Date.now() / 1000;
      //@ts-ignore
      if (decodedToken.exp < currentTime) {
        return false;
      }
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  signinSilent = async () => {
    if (this._lockSignInSilentMethod) {
      console.log("SignInSilent Function is locked");
      return;
    }
    this._lockSignInSilentMethod = true;
    this.UserManager.signinSilent()
      .then((user) => {
        // eslint-disable-next-line no-console
        console.log("Signed in", new Date());
        this._lockSignInSilentMethod = false;
        if (user && user.access_token) {
          set("accessToken", user.access_token);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log(err);
        this.signinRedirect();
      });
  };

  signinSilentCallback = () => {
    this.UserManager.signinSilentCallback();
  };

  logout = () => {
    const oidcStorage = sessionStorage.getItem(
      `oidc.user:${IDENTITY_CONFIG.authority}:${IDENTITY_CONFIG.client_id}`
    );

    if (isNotNil(oidcStorage)) {
      this.UserManager.signoutRedirect({
        id_token_hint: JSON.parse(oidcStorage)?.id_token,
      });
    } else {
      this.signoutRedirectCallback();
    }
    this.UserManager.clearStaleState();
  };

  signoutRedirect = () => {
    const oidcStorage = sessionStorage.getItem(
      `oidc.user:${IDENTITY_CONFIG.authority}:${IDENTITY_CONFIG.client_id}`
    );

    this.UserManager.signoutRedirect({
      //@ts-ignore
      id_token_hint: JSON.parse(oidcStorage)?.id_token,
    });
  };

  signoutRedirectCallback = () => {
    this.UserManager.signoutRedirectCallback().then(() => {
      localStorage.clear();
      window.location.replace("/");
    });
    this.UserManager.clearStaleState();
  };

  getCurrentDate = () => {
    var date = new Date();
    var timestamp = Math.floor(date.getTime() / 1000.0);
    return timestamp;
  };

  checkReAuth = () => {
    const oidcStorage = sessionStorage.getItem(
      `oidc.user:${IDENTITY_CONFIG.authority}:${IDENTITY_CONFIG.client_id}`
    );
    const expires_at = JSON.parse(oidcStorage!)?.expires_at;
    const current = this.getCurrentDate();
    const diff = expires_at - current;
    const differenceDate = new Date(diff * 1000);
    const diff_mins = differenceDate.getUTCMinutes();
    if (diff_mins >= 5) return false;
    return true;
  };
}

const AuthService = new AuthServices();

export { AuthService };
